import { Link } from "react-router-dom";
import { ArrowRight, Check, Compass, ClipboardList } from "lucide-react";
import { Seo } from "@/components/site/Seo";

const servicesImage =
  "https://images.unsplash.com/photo-1511632765486-a01980e01a18?auto=format&fit=crop&w=1400&q=80";

const SERVICES = [
  {
    id: "support-coordination",
    tag: "Support Coordination · Level 2",
    icon: Compass,
    title: "Support Coordination",
    intro:
      "We help you understand and apply your NDIS plan, connect with quality providers, and build skills to manage your supports.",
    points: [
      "Understand what's funded in your plan",
      "Source and compare suitable providers",
      "Draft service agreements and bookings",
      "Resolve issues and adjust supports as life changes",
      "Prepare you for plan review",
    ],
  },
  {
    id: "plan-implementation",
    tag: "Plan Implementation · Always included",
    icon: ClipboardList,
    title: "Plan Implementation",
    intro:
      "We turn the words in your NDIS plan into a working schedule of real supports, quickly and clearly.",
    points: [
      "Map plan budgets to your daily life",
      "Set up service agreements and bookings",
      "Coordinate start dates and inductions",
      "Create a simple plan summary you can share",
      "Track outcomes against your goals",
    ],
  },
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page">
      <Seo
        title="NDIS Support Coordination Services"
        description="Support Coordination and Plan Implementation services for NDIS participants across Australia. We help you understand your plan, connect with providers, and build everyday outcomes."
        path="/services"
      />
      {/* HERO */}
      <section className="container-narrow py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="fade-up">
            <span className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-700 text-xs font-medium tracking-wide uppercase px-3 py-1.5 rounded-full">
              <span className="h-1.5 w-1.5 rounded-full bg-[#1B3A6B]" aria-hidden="true" />
              Our Services · Brisbane and Australia-wide
            </span>
            <h1 className="mt-6 font-display text-4xl md:text-5xl lg:text-[3.75rem] font-medium tracking-tight text-slate-900 leading-[1.05]">
              Coordination that fits your plan and your life
            </h1>
            <p className="mt-6 text-lg md:text-xl text-slate-700 leading-relaxed">
              From your very first plan through to complex situations, we offer
              tailored support to make the NDIS work for you.
            </p>
            <Link
              to="/contact"
              data-testid="services-hero-cta"
              className="mt-8 inline-flex items-center gap-2 bg-[#1B3A6B] text-white hover:bg-[#132A4D] px-7 py-3.5 rounded-lg font-medium"
            >
              Talk to a coordinator <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <img
            src={servicesImage}
            alt="Four friends arm-in-arm watching a sunset, symbolising connection and support"
            className="w-full h-[420px] md:h-[520px] object-cover rounded-3xl shadow-sm"
            data-testid="services-hero-image"
          />
        </div>
      </section>

      {/* DETAILED SERVICES */}
      <section className="bg-white border-y border-slate-200">
        <div className="container-narrow py-16 md:py-24 space-y-16">
          {SERVICES.map((s, idx) => {
            const Icon = s.icon;
            const reverse = idx % 2 === 1;
            return (
              <article
                key={s.id}
                id={s.id}
                data-testid={`service-section-${s.id}`}
                className={`grid lg:grid-cols-12 gap-10 items-start ${
                  reverse ? "lg:[&>div:first-child]:order-2" : ""
                }`}
              >
                <div className="lg:col-span-5">
                  <div className="inline-flex items-center gap-2 bg-[#F0F4F8] text-[#1B3A6B] text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full">
                    <Icon className="h-4 w-4" aria-hidden="true" />
                    {s.tag}
                  </div>
                  <h2 className="mt-5 font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-snug">
                    {s.title}
                  </h2>
                  <p className="mt-4 text-lg text-slate-700 leading-relaxed">
                    {s.intro}
                  </p>
                </div>
                <ul className="lg:col-span-7 grid sm:grid-cols-2 gap-3">
                  {s.points.map((p) => (
                    <li
                      key={p}
                      className="flex items-start gap-3 bg-[#FDFBF7] border border-slate-200 rounded-xl p-5"
                    >
                      <Check className="h-5 w-5 text-[#2A6496] mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span className="text-[15px] text-slate-800">{p}</span>
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="container-narrow py-16 md:py-24">
        <div className="bg-white border border-slate-200/70 rounded-3xl p-10 md:p-14 grid lg:grid-cols-3 gap-8 items-center">
          <div className="lg:col-span-2">
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-slate-900 leading-snug">
              Not sure which service is right for you?
            </h2>
            <p className="mt-4 text-lg text-slate-700">
              Send us a short enquiry. We'll help you figure it out, no obligation.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="services-cta-contact"
            className="justify-self-start lg:justify-self-end inline-flex items-center gap-2 bg-[#1B3A6B] text-white hover:bg-[#132A4D] px-6 py-3.5 rounded-lg font-medium"
          >
            Start an enquiry <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
